import React from "react";
import Seo from "../components/Seo.jsx";

/* Pricing screen — Free, Solo, Growth. Catalog comes from GET /billing/plans
   so plan copy, prices, and caps never drift between backend gating and the
   UI. The screen doubles as the upgrade entry point: clicking a paid CTA
   hits /billing/checkout and redirects to Stripe's hosted Checkout. */

const PLAN_RANK = { free: 0, solo: 1, growth: 2 };

function PlanCard({ plan, current, busy, onSelect }) {
  const isPaid = plan.price_cents > 0;
  const isCurrent = current === plan.id;
  const currentRank = PLAN_RANK[current] ?? 0;
  const targetRank = PLAN_RANK[plan.id] ?? 0;
  const isDowngrade = !isCurrent && targetRank < currentRank;
  const isUpgrade = !isCurrent && targetRank > currentRank;
  // "Most Popular" only on Solo, and only when it would actually be an
  // upgrade path for the viewer — hiding it for Growth subscribers so the
  // page doesn't tell them to "upgrade" to a lower tier.
  const featured = plan.id === "solo" && currentRank <= PLAN_RANK.solo;

  return (
    <div
      className="card"
      style={{
        padding: 28,
        position: "relative",
        borderRadius: 16,
        border: featured
          ? "1.5px solid var(--accent)"
          : "1px solid var(--border)",
        boxShadow: featured
          ? "0 10px 40px -16px color-mix(in srgb, var(--accent) 40%, transparent)"
          : "none",
        background: featured
          ? "linear-gradient(180deg, color-mix(in srgb, var(--accent-subtle) 35%, var(--bg-card)) 0%, var(--bg-card) 70%)"
          : "var(--bg-card)",
        display: "flex",
        flexDirection: "column",
        gap: 18,
        minHeight: 460,
      }}
    >
      {featured && (
        <div
          style={{
            position: "absolute",
            top: -12,
            left: 24,
            padding: "4px 10px",
            background: "var(--accent)",
            color: "var(--accent-text)",
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: "uppercase",
          }}
        >
          Most Popular
        </div>
      )}

      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "var(--text-primary)" }}>
            {plan.name}
          </h3>
          {isCurrent && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: "uppercase",
                padding: "2px 7px",
                borderRadius: 999,
                background: "var(--success-subtle)",
                color: "var(--success-text)",
                border: "1px solid var(--success-border)",
              }}
            >
              Current
            </span>
          )}
        </div>
        <div style={{ marginTop: 4, fontSize: 13, color: "var(--text-secondary)" }}>
          {plan.tagline}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontSize: 38, fontWeight: 700, color: "var(--text-primary)" }}>
          {plan.price_label}
        </span>
        {plan.price_period && (
          <span style={{ fontSize: 14, color: "var(--text-tertiary)" }}>
            {plan.price_period}
          </span>
        )}
      </div>

      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
        {(plan.features || []).map((f, i) => (
          <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <span
              style={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                flexShrink: 0,
                background: featured ? "var(--accent)" : "var(--success-subtle)",
                color: featured ? "var(--accent-text)" : "var(--success-text)",
                display: "grid",
                placeItems: "center",
                marginTop: 1,
              }}
            >
              <window.I.Check size={11} />
            </span>
            <span style={{ fontSize: 13.5, color: "var(--text-primary)", lineHeight: 1.55 }}>{f}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        className={isUpgrade ? "btn btn-primary" : "btn btn-secondary"}
        style={{ justifyContent: "center", padding: "11px 16px", fontSize: 14, fontWeight: 600 }}
        onClick={onSelect}
        disabled={isCurrent || busy}
      >
        {busy ? (
          <window.I.Refresh
            size={13}
            style={{ animation: "spin 900ms linear infinite" }}
          />
        ) : isUpgrade ? (
          <window.I.Sparkles size={13} />
        ) : isDowngrade ? (
          <window.I.ArrowDown size={13} />
        ) : null}
        {isCurrent
          ? "Current plan"
          : isUpgrade
            ? "Upgrade"
            : isDowngrade
              ? "Downgrade"
              : "Start free"}
      </button>
    </div>
  );
}

function PricingScreen({ onClose }) {
  const [plans, setPlans] = React.useState(null);
  const [subscription, setSubscription] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [busyPlanId, setBusyPlanId] = React.useState("");

  const { user } = window.useCurrentUser ? window.useCurrentUser() : { user: null };
  const isAuthed = !!(window.api?.auth?.isLoggedIn?.());

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [catalog, sub] = await Promise.all([
          window.api.billing.plans(),
          isAuthed ? window.api.billing.subscription() : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setPlans(catalog?.plans || []);
        setSubscription(sub?.subscription || null);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Could not load plans.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthed]);

  const currentPlan = subscription?.plan || "free";

  const onPickPlan = async (plan) => {
    setError("");
    // Already-free user picking Free: nothing to do, just dismiss.
    if (plan.id === "free" && currentPlan === "free") {
      if (typeof onClose === "function") onClose();
      else window.location.assign("/dashboard");
      return;
    }
    if (!isAuthed) {
      // Stash intent — the welcome → login flow will land them back here.
      try { localStorage.setItem("cn_post_login_action", `upgrade:${plan.id}`); } catch (e) {}
      window.location.assign("/login");
      return;
    }

    // Confirm destructive direction changes. Upgrades go through silently
    // (low-regret, the user explicitly picked a higher tier); downgrades
    // shift billing or schedule cancellation, so they deserve a prompt.
    const isDowngrade = (PLAN_RANK[plan.id] ?? 0) < (PLAN_RANK[currentPlan] ?? 0);
    if (isDowngrade) {
      const msg = plan.id === "free"
        ? "Downgrade to Free? Your paid features stay active until the end of your current billing period, then your account drops to the Free plan."
        : `Downgrade to ${plan.name}? Stripe will apply a prorated credit for the unused portion of your current plan.`;
      if (!window.confirm(msg)) return;
    }

    setBusyPlanId(plan.id);
    try {
      const resp = await window.api.billing.checkout({
        planId: plan.id,
        email: user?.email || null,
      });
      // resp.url means we redirected to Stripe Checkout — control never
      // returns here. Everything else is an in-place server-side change.
      if (resp?.url) return;

      // Refetch so the page reflects the new plan / scheduled cancel.
      try { window.bumpPlanRefresh?.(); } catch (e) {}
      try {
        const fresh = await window.api.billing.subscription();
        setSubscription(fresh?.subscription || null);
      } catch (e) {}
      setBusyPlanId("");
    } catch (e) {
      setBusyPlanId("");
      setError(e?.message || "Could not change plan.");
    }
  };

  return (
    <div className="page screen-enter" style={{ maxWidth: 920 }}>
      <Seo
        title="Pricing and plans"
        description="Free, Solo, or Growth. Pick a Community Ninja plan that matches how serious you are about Reddit as a customer-acquisition channel."
        keywords={["reddit monitoring pricing", "community ninja plans", "reddit tool pricing", "founder marketing tools"]}
      />

      <div className="page-header" style={{ textAlign: "center", marginBottom: 32, display: "block" }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            borderRadius: 999,
            background: "var(--accent-subtle)",
            color: "var(--accent-soft-text)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            marginBottom: 14,
          }}
        >
          <window.I.Sparkles size={11} />
          Plans
        </div>
        <h1 className="page-title" style={{ fontSize: 32 }}>
          Pick a plan that matches your ambition
        </h1>
        <p className="page-subtitle" style={{ maxWidth: 540, margin: "10px auto 0" }}>
          Start free, upgrade when Reddit starts shipping leads. Cancel any time.
          anytime, no contracts, no annual lock-ins.
        </p>
      </div>

      {error && (
        <div
          style={{
            margin: "0 auto 18px",
            maxWidth: 540,
            padding: "10px 14px",
            background: "var(--danger-subtle)",
            border: "1px solid var(--danger-border)",
            color: "var(--danger-text)",
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {loading || !plans ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 20,
          }}
        >
          {[0, 1, 2].map((i) => (
            <div key={i} className="card" style={{ padding: 28, minHeight: 460 }}>
              <div className="skel" style={{ height: 22, width: "55%", marginBottom: 12 }} />
              <div className="skel" style={{ height: 12, width: "80%", marginBottom: 24 }} />
              <div className="skel" style={{ height: 36, width: "40%", marginBottom: 24 }} />
              {[0, 1, 2, 3, 4].map((j) => (
                <div className="skel" key={j} style={{ height: 12, width: "92%", marginBottom: 12 }} />
              ))}
              <div className="skel" style={{ height: 40, width: "100%", marginTop: 18, borderRadius: 8 }} />
            </div>
          ))}
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 20,
            alignItems: "stretch",
          }}
        >
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              current={currentPlan}
              busy={busyPlanId === plan.id}
              onSelect={() => onPickPlan(plan)}
            />
          ))}
        </div>
      )}

      <div
        style={{
          marginTop: 36,
          padding: 22,
          borderRadius: 14,
          background: "var(--bg-sunken)",
          border: "1px dashed var(--border)",
          display: "grid",
          gap: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <window.I.ShieldCheck size={14} style={{ color: "var(--text-secondary)" }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
            Questions you probably have
          </span>
        </div>
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          <li style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>
            <strong style={{ color: "var(--text-primary)" }}>Cancel any time.</strong>{" "}
            Manage your subscription from Settings → Subscription. You keep access until the end of the period.
          </li>
          <li style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>
            <strong style={{ color: "var(--text-primary)" }}>We never post on your behalf.</strong>{" "}
            Community Ninja is read-only. We surface the threads, you write the replies.
          </li>
        </ul>
      </div>
    </div>
  );
}

window.PricingScreen = PricingScreen;
